import type {
  ServiceSchema,
  UpdateServiceSchema,
  VatRateSchema,
} from "@repo/validators";
import type { z } from "zod";
import { useEffect } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { VatRate } from "~/providers/general-provider";
import { api } from "~/lib/api";
import { useVatRateStore } from "~/stores/use-vat-rate-store";
import { usePermissionManager } from "./use-permission-manager";

export const useServiceManager = () => {
  const { mutate: createService } = api.service.createService.useMutation();
  const { mutate: updateService } = api.service.updateService.useMutation({
    onSuccess() {
      toast.success(
        capitalize(t("update_item_success", { item: t("Services.service") })),
      );
    },
  });
  const { hasPermission } = usePermissionManager();

  const { t } = useTranslation("common");

  const handleUpdateService = async (
    data: z.infer<ReturnType<typeof UpdateServiceSchema>>,
  ) => {
    return new Promise<boolean>((resolve) => {
      updateService(data, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const handleCreateService = async (
    data: z.infer<ReturnType<typeof ServiceSchema>>,
  ) => {
    return new Promise<boolean>((resolve) => {
      createService(data, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  return {
    createService: handleCreateService,
    updateService: handleUpdateService,
  };
};
